import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../store/hook';
import {
  StudentStatusEnum,
  studentType,
} from '../../../constants/types/student-type';
import StudentStateControl from './StudentStateControl';
import { Pagination, Table } from 'antd';
import columns from './columns';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PaginationProps } from '../../../constants/types';
import StudentConroller from './StudentController';
import ClassGroupAssignmentDialog from './popup/ClassGroupAssignmentDialog';
import StudentTransferRequestDialog from './popup/StudentTransferRequestDialog';

interface StudentListProps {
  status: StudentStatusEnum;
  data: studentType[];
  pageInfo: PaginationProps;
  isLoading: boolean;
  reloadStudentList: () => void;
  /** 처리 대기 중인 이관 신청이 있는 학생 */
  pendingTransferStudentIds: Set<number>;
  reloadTransferRequests: () => void;
}

/** LMS > 학생 관리 > 학생 목록 테이블 */
const StudentList = ({
  status,
  data,
  pageInfo,
  isLoading,
  reloadStudentList,
  pendingTransferStudentIds,
  reloadTransferRequests,
}: StudentListProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { classId } = useParams<{ classId: string }>();
  const [selectedStudents, setSelectedStudents] = useState<studentType[]>([]);
  /** 반 배정 팝업에 넘길 대상 학생. null 이면 팝업이 닫힌 상태. */
  const [assignStudents, setAssignStudents] = useState<studentType[] | null>(
    null,
  );
  /** 이관 신청 팝업에 넘길 대상 학생. null 이면 팝업이 닫힌 상태. */
  const [transferStudent, setTransferStudent] = useState<studentType | null>(
    null,
  );

  const controller = useMemo(() => {
    return new StudentConroller(
      dispatch,
      classId,
      reloadStudentList,
      setAssignStudents,
      setTransferStudent,
      navigate,
    );
  }, [classId, reloadStudentList, navigate]);

  useEffect(() => {
    setSelectedStudents([]);
  }, [data]);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedStudents(
        data.filter((item) => selectedRowKeys.includes(item.id)),
      );
    },
    selectedRowKeys: selectedStudents.map((item) => item.id),
  };

  return (
    <div>
      {/* 상태별 컨트롤 버튼 */}
      <StudentStateControl
        status={status}
        reloadTable={reloadStudentList}
        studentList={selectedStudents}
        controller={controller}
      />

      <Table
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        columns={columns(
          pageInfo,
          classId,
          status,
          controller,
          pendingTransferStudentIds,
        )}
        dataSource={data}
        rowKey={(record) => record.id}
        scroll={{ x: 'max-content' }}
        pagination={false}
        size="small"
        loading={isLoading}
      />

      <Pagination
        current={pageInfo.page}
        total={pageInfo.total}
        pageSize={pageInfo.pageSize}
        showSizeChanger
        onChange={(page, pageSize) => {
          searchParams.set('page', page.toString());
          searchParams.set('pageSize', pageSize.toString());
          setSearchParams(searchParams);
        }}
        className="mt-5 justify-center"
      />

      {/* 반 배정 */}
      <ClassGroupAssignmentDialog
        open={assignStudents !== null}
        onClose={() => setAssignStudents(null)}
        classId={classId}
        studentList={assignStudents ?? []}
        reloadTable={reloadStudentList}
      />

      {/* 이관 신청 */}
      <StudentTransferRequestDialog
        open={transferStudent !== null}
        onClose={() => setTransferStudent(null)}
        student={transferStudent}
        reloadTransferRequests={reloadTransferRequests}
      />
    </div>
  );
};

export default StudentList;
